// @ts-ignore: If you see a type error for 'leaflet', run: yarn add leaflet @types/leaflet
import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
// @ts-ignore
import "./leaflet-plugin-selection";
import "./leaflet-plugin-selection.scss";
import { useSelector } from "react-redux";
import { UserState } from "redux-context/user";

// Fix Leaflet's default icon path issue with bundlers
// @ts-ignore
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const blueIcon = L.icon({
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

interface Venue {
  uuid: string;
  name: string;
  address: string;
  addressDisplay?: string;
  addressCoordinates?: {
    coordinates: [number, number]; // [lng, lat]
    type: string;
  };
}

interface VenueMapProps {
  venues: Venue[];
  selectedVenueUuids: string[];
  onSelectionChange: (uuids: string[]) => void;
}

const VenueMap: React.FC<VenueMapProps> = ({
  venues,
  selectedVenueUuids,
  onSelectionChange,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.Marker[]>([]);
  const areaSelectRef = useRef<any>(null);
  const { location } = useSelector((state: UserState) => state.user);
  // Unique id for the map container to force remount if needed
  const mapContainerId = useRef(
    `venue-map-${Math.random().toString(36).substr(2, 9)}`
  );

  // Helper: get [lat, lng] from venue
  const getLatLng = (venue: Venue): [number, number] | null => {
    if (
      venue.addressCoordinates &&
      Array.isArray(venue.addressCoordinates.coordinates)
    ) {
      // GeoJSON order is [lng, lat]
      const [lng, lat] = venue.addressCoordinates.coordinates;
      if (isNaN(lat) || isNaN(lng)) {
        console.warn(
          `Invalid coordinates for venue ${venue.name} id${
            venue.uuid
          }: coordinates ${JSON.stringify(
            venue.addressCoordinates.coordinates,
            null,
            2
          )} [${lat}, ${lng}]`
        );
        return null;
      }

      return [lat, lng];
    }
    return null;
  };

  useEffect(() => {
    const mapContainer = mapRef.current;
    if (!mapContainer || venues.length === 0) return;

    // Clean up previous map if any
    if (leafletMapRef.current) {
      leafletMapRef.current.remove();
      leafletMapRef.current = null;
    }
    // Clear the container's innerHTML to ensure a fresh DOM node
    mapContainer.innerHTML = "";
    // Center map on first venue with coordinates

    const hasLocation = location?.latitude && location?.longitude;
    const initialLatLng = hasLocation
      ? [location.latitude, location.longitude]
      : [40.7128, -74.006]; // Default to NYC
    const map = L.map(mapContainer, {
      attributionControl: false,
    }).setView(initialLatLng as [number, number], 12);
    leafletMapRef.current = map;

    // Add tile layer
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors",
    }).addTo(map);

    // Add markers
    markersRef.current = venues
      .map((venue) => {
        const latlng = getLatLng(venue);
        if (!latlng?.length) return null;
        const marker = L.marker(latlng, {
          title: venue.name,
          opacity: selectedVenueUuids.includes(venue.uuid) ? 1 : 0.1,
          icon: blueIcon,
        }).addTo(map);
        marker.bindPopup(
          `<b>${venue.name}</b><br/>${venue.addressDisplay || venue.address}`
        );
        marker.on("click", () => {
          let newSelection;
          if (selectedVenueUuids.includes(venue.uuid)) {
            newSelection = selectedVenueUuids.filter((id) => id !== venue.uuid);
          } else {
            newSelection = [...selectedVenueUuids, venue.uuid];
          }
          onSelectionChange(newSelection);
        });
        return marker;
      })
      .filter(Boolean) as L.Marker[];

    // Add AreaSelect
    // @ts-ignore
    const areaSelect = L.areaSelect({ width: 300, height: 300 });
    areaSelect.addTo(map);
    areaSelectRef.current = areaSelect;

    // Select venues inside the initial area box
    const initialBounds = areaSelect.getBounds();
    const initiallySelected = venues
      .filter((venue) => {
        const latlng = getLatLng(venue);
        if (!latlng) return false;
        return initialBounds.contains(latlng as [number, number]);
      })
      .map((venue) => venue.uuid);
    if (initiallySelected.length > 0) {
      onSelectionChange(initiallySelected);
    }

    areaSelect.on("change", function () {
      const bounds = areaSelect.getBounds();
      const selected = venues
        .filter((venue) => {
          const latlng = getLatLng(venue);
          if (!latlng) return false;
          return bounds.contains(latlng as [number, number]);
        })
        .map((venue) => venue.uuid);
      onSelectionChange(selected);
    });

    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
      markersRef.current = [];
      areaSelectRef.current = null;
      if (mapContainer) mapContainer.innerHTML = "";
    };
    // eslint-disable-next-line
  }, [venues]);

  // Update marker opacity when selection changes
  useEffect(() => {
    markersRef.current.forEach((marker, idx) => {
      const venue = venues[idx];
      if (venue && marker) {
        marker.setOpacity(selectedVenueUuids.includes(venue.uuid) ? 1 : 0.5);
      }
    });
  }, [selectedVenueUuids, venues]);

  return (
    <div
      ref={mapRef}
      id={mapContainerId.current}
      style={{ height: 400, width: "100%", marginBottom: 24 }}
    />
  );
};

export default VenueMap;
